import { useEffect, useRef, useState } from "react";
import { Layout } from "../../components/layout/Layout";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useUnreadMessages } from "../../context/UnreadMessagesContext";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Paperclip, Send, CheckCheck } from "lucide-react";

// Fréquence de rafraîchissement automatique (ms). Suffisamment rapide pour que la
// réponse de l'interlocuteur apparaisse sans avoir à recharger la page, sans pour
// autant multiplier les appels réseau.
const POLL_INTERVAL_MS = 3000;
const UNREAD_POLL_INTERVAL_MS = 4000;

export default function MessagesPage() {
  const { user } = useAuth();
  const { refresh: refreshSidebarUnread, markAllRead: markAllReadGlobal } = useUnreadMessages();
  const [users, setUsers] = useState([]);
  const [markingAll, setMarkingAll] = useState(false);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  // Nombre de messages non lus par expéditeur — { [user_id]: count } — pilote le
  // point rouge affiché sur chaque personne dans la liste de gauche.
  const [unreadCounts, setUnreadCounts] = useState({});
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const prevMsgCountRef = useRef(0);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.id !== user.id)));
  }, []);

  const loadUnreadCounts = async () => {
    try {
      const { data } = await api.get("/communication/messages/unread-counts");
      setUnreadCounts(data);
    } catch (e) {
      // silencieux : ne doit pas interrompre l'usage de la messagerie
    }
    // Garde aussi le badge de la barre de navigation synchronisé immédiatement
    // (plutôt que d'attendre son prochain sondage automatique).
    refreshSidebarUnread();
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      await markAllReadGlobal();
      setUnreadCounts({});
      toast.success("Tous les messages ont été marqués comme lus");
    } catch (e) {
      // silencieux : action de confort, un échec ponctuel n'est pas bloquant
    } finally {
      setMarkingAll(false);
    }
  };

  // Rafraîchit les compteurs "non lu" en tâche de fond, indépendamment de la
  // conversation actuellement ouverte, pour que le point rouge apparaisse dès
  // qu'un nouveau message arrive d'une autre personne.
  useEffect(() => {
    loadUnreadCounts();
    const t = setInterval(loadUnreadCounts, UNREAD_POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  // Charge le fil de la conversation active. Ouvrir une conversation marque côté
  // serveur les messages reçus comme lus (voir backend), donc chaque appel ici
  // rafraîchit aussi les compteurs pour faire disparaître le point rouge.
  const loadMessages = async (uid) => {
    try {
      const { data } = await api.get(`/communication/messages?with_user=${uid}`);
      setMessages(data);
    } catch (e) {
      // silencieux : un échec ponctuel de sondage ne doit pas planter l'écran
    }
    loadUnreadCounts();
  };

  const selectUser = (uid) => {
    setActive(uid);
    setMessages([]);
    prevMsgCountRef.current = 0;
  };

  // Rafraîchissement automatique du fil ouvert : c'est ce qui fait apparaître la
  // réponse de l'interlocuteur sans avoir à recharger la page.
  useEffect(() => {
    if (!active) return;
    loadMessages(active);
    const t = setInterval(() => loadMessages(active), POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ne fait défiler vers le bas que lorsque le nombre de messages a réellement
  // changé (nouvel envoi ou nouvelle réponse reçue) — pas à chaque sondage
  // silencieux qui ne ramène rien de nouveau.
  useEffect(() => {
    if (messages.length !== prevMsgCountRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      prevMsgCountRef.current = messages.length;
    }
  }, [messages]);

  const send = async () => {
    if (!text.trim() || !active) return;
    await api.post("/communication/messages", { to_user_id: active, content: text });
    setText("");
    loadMessages(active);
  };

  const sendFile = async (e) => {
    const file = e.target.files[0];
    if (!file || !active) return;
    const fd = new FormData();
    fd.append("to_user_id", active);
    fd.append("file", file);
    await api.post("/communication/messages/attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
    loadMessages(active);
  };

  return (
    <Layout title="Messagerie interne">
      <div className="grid grid-cols-4 gap-4 h-[calc(100vh-160px)]">
        <div className="rounded-xl border border-border bg-card overflow-y-auto flex flex-col" data-testid="messages-user-list">
          <div className="p-2 border-b border-border shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="w-full gap-1.5"
              onClick={markAllRead}
              disabled={markingAll}
              data-testid="messages-mark-all-read-button"
            >
              <CheckCheck size={14} /> Tout marquer comme lu
            </Button>
          </div>
          {users.map((u) => (
            <button
              key={u.id}
              data-testid={`messages-user-${u.id}`}
              onClick={() => selectUser(u.id)}
              className={`w-full flex items-center justify-between gap-2 text-left px-4 py-3 border-b border-border text-sm hover:bg-accent transition-colors duration-200 ${active === u.id ? "bg-accent" : ""}`}
            >
              <div className="min-w-0">
                <p className={`truncate ${unreadCounts[u.id] ? "font-semibold" : "font-medium"}`}>{u.prenom} {u.nom}</p>
                <p className="text-xs text-muted-foreground truncate">{u.poste}</p>
              </div>
              {unreadCounts[u.id] > 0 && (
                <span
                  className="shrink-0 min-w-[9px] h-[9px] rounded-full bg-red-500"
                  title={`${unreadCounts[u.id]} message(s) non lu(s)`}
                  data-testid={`messages-unread-dot-${u.id}`}
                />
              )}
            </button>
          ))}
        </div>
        <div className="col-span-3 rounded-xl border border-border bg-card flex flex-col">
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-thread">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.from_user_id === user.id ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-sm px-3 py-2 rounded-xl text-sm ${m.from_user_id === user.id ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                  {m.content && <p>{m.content}</p>}
                  {m.attachment_url && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${m.attachment_url}`} target="_blank" rel="noreferrer" className="underline text-xs flex items-center gap-1">
                      <Paperclip size={12} /> {m.attachment_name}
                    </a>
                  )}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          {active && (
            <div className="p-3 border-t border-border flex gap-2">
              <input type="file" ref={fileRef} className="hidden" onChange={sendFile} data-testid="messages-file-input" />
              <Button variant="outline" size="icon" onClick={() => fileRef.current.click()} data-testid="messages-attach-button"><Paperclip size={16} /></Button>
              <Input data-testid="messages-text-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Votre message..." />
              <Button onClick={send} data-testid="messages-send-button"><Send size={16} /></Button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
